import { ApostleLite, ApostleSortBy } from '@apeiron/apostle'
import { BOUNTY_REWARD_MINUS_FRACTION } from '@apeiron/bounty'
import { ListingStatus } from '@apeiron/common'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { MayNull, noForwardProps, useWeb3 } from '@apeiron/temp'
import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import useGetAuctionPrice from '@src/hooks/apeiron/trade/useGetAuctionPrice'
import useGetApostles from '@src/hooks/graphql/useGetApostles'
import Decimal from 'decimal.js'
import * as R from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const InvalidFloorDiff = '-100%'

const StyledTableContainer = styled(TableContainer)`
  margin: 16px auto;
`

const StyledTableBody = styled(TableBody)`
  background: linear-gradient(99deg, #1e1e1e -0.18%, #000 78.14%);
  box-shadow: 0px -1px 0px 0px #27272a inset;
`

const StyledTableRow = styled(TableRow)`
  td,
  th {
    border-color: rgba(255, 255, 255, 0.2);
  }

  :last-child td,
  &:last-child th {
    border: 0;
  }
`

const TableTopRow = styled(TableRow)`
  background: #000;

  th,
  td {
    border: 0;
  }
`

const AmountTableCell = styled(TableCell)`
  text-align: right;
  padding: 4px 16px;
`

const StyledTableCellHead = styled(TableCell)``

const StyledTableCell = styled(TableCell)`
  text-align: right;
`

const FloorDiff = styled(Typography, noForwardProps)<PositiveProps>`
  color: ${props => {
    if (props.__invalid) {
      return '#c0811b'
    }

    return props.__postive ? '#39ce02' : '#fe5c01'
  }};
  font-size: 14px;
`

const ClaimBountyPriceTable = ({
  unitPrice,
  quantity,
  currencyType
}: Props) => {
  const { convertCurrencyToUsd, getApeironPriceWithUnit, getCurrency } =
    useCurrencyContext()

  const { fromWei } = useWeb3()

  const {
    data: apostles,
    fetch: fetchApostles,
    loading: loadingApostles
  } = useGetApostles()

  const { t } = useTranslation()

  const displayUnitPrice = getApeironPriceWithUnit(currencyType, unitPrice)

  const totalPriceBigInt = BigInt(unitPrice) * BigInt(quantity)

  const displayTotal = getApeironPriceWithUnit(
    currencyType,
    totalPriceBigInt.toString()
  )

  const { getCurrentPrice } = useGetAuctionPrice()

  const removeZero = useCallback((input: string): string => {
    return input.replace(/(\.\d*?[1-9])0+|\.0*$/, '$1')
  }, [])

  const floorPriceDiff = useMemo(() => {
    const floorApostle: MayNull<ApostleLite> = R.propOr(null, '0', apostles)

    if (R.isNotNil(floorApostle?.listingInfo) && unitPrice !== '0') {
      const { listingInfo } = floorApostle

      const floorPrice = getCurrentPrice({
        currencyType: listingInfo.currencyType,
        duration: listingInfo.duration,
        endPrice: listingInfo.endPrice,
        startDate: listingInfo.startDate,
        startPrice: listingInfo.startPrice
      })

      const currency = getCurrency(currencyType)

      const floorUSD = convertCurrencyToUsd(
        floorPrice,
        listingInfo.currencyType
      )

      const bountyPrice = parseFloat(
        fromWei(unitPrice, currency?.decimals || 18)
      )

      const bountyUSD = convertCurrencyToUsd(bountyPrice, currencyType)

      const percent = ((bountyUSD - floorUSD) / floorUSD) * 100

      const cleanPercent = removeZero(percent.toFixed(2))

      return percent >= 0 ? `+${cleanPercent}%` : `${cleanPercent}%`
    }

    return InvalidFloorDiff
  }, [
    apostles,
    currencyType,
    unitPrice,
    convertCurrencyToUsd,
    fromWei,
    getCurrency,
    getCurrentPrice,
    removeZero
  ])

  const yourReward = new Decimal(unitPrice)
    .mul(quantity)
    .mul(1 - BOUNTY_REWARD_MINUS_FRACTION)

  const displayReward = getApeironPriceWithUnit(
    currencyType,
    yourReward.toString()
  )

  useEffect(() => {
    fetchApostles({
      filterInput: {
        nftListingStatus: [ListingStatus.LISTING]
      },
      sortInput: {
        sortBy: ApostleSortBy.PriceAsc
      },
      pageInput: {
        pageSize: 1
      }
    })
  }, [])

  return (
    <StyledTableContainer>
      <Table>
        <TableHead>
          <TableTopRow>
            <AmountTableCell scope='col' colSpan={2}>
              {t('bounty.apostle.claim.table.amount')}
            </AmountTableCell>
          </TableTopRow>
        </TableHead>
        <StyledTableBody>
          <StyledTableRow>
            <StyledTableCellHead component='th' scope='row'>
              {t('bounty.apostle.claim.table.bounty_reward')}
            </StyledTableCellHead>
            <StyledTableCell>
              {displayUnitPrice}
              {!loadingApostles && (
                <FloorDiff
                  __invalid={unitPrice === '0'}
                  __postive={floorPriceDiff?.startsWith('+')}
                >
                  {t('bounty.from_floor', { percent: floorPriceDiff })}
                </FloorDiff>
              )}
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCellHead component='th' scope='row'>
              {t('bounty.apostle.claim.table.quantity')}
            </StyledTableCellHead>
            <StyledTableCell>{quantity}</StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCellHead component='th' scope='row'>
              {t('bounty.apostle.claim.table.total')}
            </StyledTableCellHead>
            <StyledTableCell>{displayTotal}</StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCellHead component='th' scope='row'>
              {t('bounty.apostle.claim.table.your_rewards')}
            </StyledTableCellHead>
            <StyledTableCell>{displayReward}</StyledTableCell>
          </StyledTableRow>
        </StyledTableBody>
      </Table>
    </StyledTableContainer>
  )
}

type PositiveProps = {
  __invalid?: boolean
  __postive?: boolean
}

type Props = {
  unitPrice: string
  quantity: number
  currencyType: CurrencyTokenType
}

export default ClaimBountyPriceTable
